import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Section, Eyebrow } from "@/components/kit";
import { Reveal } from "@/components/motion";
import { LEAD_CONFIGS } from "@/data/leadConfigs";
import { postContact } from "@/lib/api";

// CTA/formulario reutilizable por sección. Envía por correo incluyendo la página de origen.
export const LeadSection = ({ path, alt = false }) => {
  const cfg = LEAD_CONFIGS[path];
  const [values, setValues] = useState({});
  const [combo, setCombo] = useState(cfg?.combos?.[0] || "");
  const [loading, setLoading] = useState(false);

  if (!cfg) return null;
  const accent = cfg.accent;
  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const extraFields = (cfg.extra || [])
        .filter((f) => values[f.name])
        .map((f) => ({ label: f.label, value: values[f.name] }));
      if (values.mensaje) extraFields.push({ label: "Mensaje", value: values.mensaje });
      await postContact({
        sourcePath: path,
        name: values.nombre || "",
        email: values.email || "",
        combo,
        fields: extraFields,
      });
      toast.success("¡Recibido! Te contactamos pronto.", {
        description: "Gracias por sumarte a la aventura.",
      });
      setValues({});
    } catch (err) {
      toast.error(err.message || "No se pudo enviar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-ink/10 bg-white px-4 py-3 text-ink outline-none transition-all focus:border-transparent focus:ring-2";

  return (
    <Section alt={alt} id="contacto">
      <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr]">
        <Reveal>
          <Eyebrow color={accent}>{cfg.eyebrow}</Eyebrow>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold leading-none tracking-tighter text-ink">
            {cfg.title}
          </h2>
          <p className="mt-6 max-w-md text-lg text-ink/70 leading-relaxed">{cfg.text}</p>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-ink/40">
            Enviamos tu mensaje directo al equipo
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            className="rounded-[2rem] bg-white p-7 sm:p-8 shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
            data-testid="lead-form"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-ink/60">
                  Nombre
                </label>
                <input
                  data-testid="lead-nombre"
                  required
                  value={values.nombre || ""}
                  onChange={(e) => set("nombre", e.target.value)}
                  placeholder="Tu nombre"
                  className={inputCls}
                  style={{ "--tw-ring-color": accent }}
                />
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-ink/60">
                  Email
                </label>
                <input
                  data-testid="lead-email"
                  required
                  type="email"
                  value={values.email || ""}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="tucorreo@email.com"
                  className={inputCls}
                  style={{ "--tw-ring-color": accent }}
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-ink/60">
                ¿Qué te interesa?
              </label>
              <select
                data-testid="lead-combo"
                value={combo}
                onChange={(e) => setCombo(e.target.value)}
                className={inputCls}
                style={{ "--tw-ring-color": accent }}
              >
                {cfg.combos.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {(cfg.extra || []).map((f) => (
              <div className="mt-4" key={f.name}>
                <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-ink/60">
                  {f.label}
                </label>
                <input
                  data-testid={`lead-${f.name}`}
                  value={values[f.name] || ""}
                  onChange={(e) => set(f.name, e.target.value)}
                  placeholder={f.placeholder}
                  className={inputCls}
                  style={{ "--tw-ring-color": accent }}
                />
              </div>
            ))}

            <div className="mt-4">
              <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-ink/60">
                Mensaje
              </label>
              <textarea
                data-testid="lead-mensaje"
                rows={3}
                value={values.mensaje || ""}
                onChange={(e) => set("mensaje", e.target.value)}
                placeholder="Cuéntanos más..."
                className={`${inputCls} resize-none`}
                style={{ "--tw-ring-color": accent }}
              />
            </div>

            <button
              type="submit"
              data-testid="lead-submit"
              disabled={loading}
              className="mt-6 group inline-flex w-full items-center justify-center gap-2 rounded-full px-7 py-3.5 font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-70"
              style={{ backgroundColor: accent }}
            >
              {loading ? "Enviando..." : "Enviar"}
              <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
          </form>
        </Reveal>
      </div>
    </Section>
  );
};
